import { ReactNode, CSSProperties } from "react";
import { Progress, Tooltip } from "antd";
import { CheckCircleOutlined, RightOutlined } from "@ant-design/icons";
import moment from "moment";
import { normalize } from "../../../../../common/utils/TextUtils";

const getKeywordTokens = (keyword?: string | null) =>
    (keyword || '')
        .trim()
        .split(/\s+/)
        .map((token) => token.trim())
        .filter((token) => normalize(token).length > 1)
        .sort((a, b) => normalize(b).length - normalize(a).length);

const buildNormalizedIndexMap = (text: string) => {
    const startMap: number[] = [];
    const endMap: number[] = [];
    let normalized = '';
    let i = 0;

    while (i < text.length) {
        const codePoint = text.codePointAt(i)!;
        const char = String.fromCodePoint(codePoint);
        const utf16Len = codePoint > 0xffff ? 2 : 1;
        const origStart = i;
        const origEnd = i + utf16Len;
        const normalizedChar = normalize(char);

        for (let j = 0; j < normalizedChar.length; j++) {
            startMap.push(origStart);
            endMap.push(origEnd);
            normalized += normalizedChar[j];
        }

        i += utf16Len;
    }

    return { normalized, startMap, endMap };
};

export const highlightKeywordMatches = (text: string, keyword?: string | null, bold = true) => {
    const tokens = getKeywordTokens(keyword);
    if (!text || !tokens.length) return text;

    const escaped = tokens
        .map((token) => normalize(token).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .filter(Boolean);
    if (!escaped.length) return text;

    const { normalized, startMap, endMap } = buildNormalizedIndexMap(text);
    const regex = new RegExp(escaped.join('|'), 'g');
    const ranges: { start: number, end: number }[] = [];
    let match: RegExpExecArray | null;

    while ((match = regex.exec(normalized)) !== null) {
        if (!match[0].length) {
            regex.lastIndex += 1;
            continue;
        }
        const normStart = match.index;
        const normEnd = match.index + match[0].length - 1;
        ranges.push({
            start: startMap[normStart],
            end: endMap[normEnd],
        });
    }

    if (!ranges.length) return text;

    ranges.sort((a, b) => a.start - b.start);
    const merged: { start: number, end: number }[] = [];
    ranges.forEach((range) => {
        const last = merged[merged.length - 1];
        if (!last || range.start > last.end) {
            merged.push({ ...range });
        } else {
            last.end = Math.max(last.end, range.end);
        }
    });

    const parts: ReactNode[] = [];
    let cursor = 0;
    merged.forEach((range, index) => {
        if (cursor < range.start) {
            parts.push(<span key={`t-${index}`}>{text.slice(cursor, range.start)}</span>);
        }
        parts.push(
            <mark
                key={`m-${index}`}
                style={{
                    background: 'rgba(250, 204, 21, 0.35)',
                    color: 'inherit',
                    padding: '0 2px',
                    borderRadius: 3,
                    fontWeight: bold ? 700 : 400,
                }}
            >
                {text.slice(range.start, range.end)}
            </mark>
        );
        cursor = range.end;
    });
    if (cursor < text.length) {
        parts.push(<span key="end">{text.slice(cursor)}</span>);
    }
    return parts;
};

export const KeywordMatchIcon = ({ fieldLabel, keyword }: { fieldLabel: string, keyword?: string | null }) => (
    <Tooltip
        title={
            <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Keyword en {fieldLabel.toLowerCase()}</div>
                <div style={{ fontSize: 12, opacity: 0.9 }}>
                    {keyword
                        ? <>La keyword <b>«{keyword}»</b> aparece en el {fieldLabel.toLowerCase()} del negocio.</>
                        : <>La keyword aparece en el {fieldLabel.toLowerCase()} del negocio.</>}
                </div>
            </div>
        }
    >
        <CheckCircleOutlined style={{
            fontSize: 13,
            color: 'teal',
            flexShrink: 0,
            cursor: 'default',
        }} />
    </Tooltip>
);

export const getDistanceProximity = (meters: number) => {
    if (meters <= 1000) return 'Cercano';
    if (meters <= 2000) return 'Lejano';
    return 'Muy lejano';
};

export const calcVisibilityRadius = (
    radius: number,
    green?: number | null,
    total?: number | null,
    presenceRatio?: number,
) => {
    const gridRadius = Number(radius || 0);
    if (!gridRadius) return 0;

    let ratioFactor = 0;
    if (green != null && total != null && total > 0 && green > 0) {
        ratioFactor = Math.sqrt(green) / Math.sqrt(total);
    } else if ((presenceRatio ?? 0) > 0) {
        ratioFactor = Math.sqrt(presenceRatio!);
    }

    // radio del grid × 1.2 × √puntos_verdes / √total
    return Math.round(gridRadius * 1.2 * ratioFactor);
};

/**
 * Color agrupado por radio de visibilidad (metros):
 * 0 rojo · <400 darkred · 400–1000 morado · 1000–2000 royalblue · 2000–3000 teal · >3000 verde
 */
export const getVisibilityRadiusColor = (meters: number) => {
    const value = Number(meters || 0);
    if (value <= 0) return '#dc2626';
    if (value < 400) return 'darkred';
    if (value < 1000) return '#8d1e8f';
    if (value < 2000) return 'royalblue';
    if (value < 3000) return '#0d9488';
    return '#16a34a';
};

export const getVisibilityRadiusBadgeStyle = (color: string, compact = false): CSSProperties => ({
    color,
    fontWeight: 700,
    fontSize: compact ? 12 : 17,
    lineHeight: 1.15,
    whiteSpace: 'nowrap',
    background: `color-mix(in srgb, ${color} 6%, white)`,
    border: `0.5px solid ${color}`,
    borderRadius: 6,
    padding: compact ? '2px 6px' : '2px 8px',
    display: 'inline-block',
});

export const getLocalpackPresenceMetrics = (metric: any, points?: any[]) => {
    let green: number | null = null;
    let total: number | null = null;
    let presenceRatio = metric?.localPackPercent ?? 0;

    if (points?.length) {
        total = points.length;
        green = points.filter((point: any) => {
            const pos = point.mainPlace?.positionPopularity;
            return pos != null && pos <= 3;
        }).length;
        presenceRatio = total > 0 ? green / total : 0;
    } else {
        const radiusParts = metric?.radiusParts ?? metric?.grid ?? metric?.batch?.grid;
        if (radiusParts != null) {
            total = Math.pow(2 * radiusParts + 1, 2);
            green = Math.round(presenceRatio * total);
        } else if (metric?.localPackPoints != null && metric?.totalPoints != null) {
            green = metric.localPackPoints;
            total = metric.totalPoints;
            presenceRatio = total > 0 ? green / total : presenceRatio;
        }
    }

    const gridRadius = Number(
        metric?.radius ?? metric?.distance ?? metric?.batch?.distance ?? 0
    );
    const presencePercent = Math.round(100 * presenceRatio);
    const visibilityRadius = calcVisibilityRadius(gridRadius, green, total, presenceRatio);
    const percentColor = getVisibilityRadiusColor(visibilityRadius);

    return { green, total, presencePercent, visibilityRadius, percentColor };
};

export const LocalpackMetricsCard = (props: {
    green?: number | null,
    total?: number | null,
    presencePercent: number,
    visibilityRadius?: number | null,
    percentColor: string,
    launchedAt?: string | null,
    compact?: boolean,
    showPoints?: boolean,
    hideVisibilityRadius?: boolean,
    onClick?: () => void,
    trailing?: ReactNode,
}) => {
    const compact = !!props.compact;
    const clickable = !!props.onClick;
    const showPoints = !!props.showPoints && props.green != null && props.total != null;
    const showVisibility = !props.hideVisibilityRadius && props.visibilityRadius != null;
    const launchedRelative = props.launchedAt
        ? moment.utc(props.launchedAt).local().fromNow()
        : null;

    const baseStyle: CSSProperties = {
        width: '100%',
        height: '100%',
        padding: compact ? '4px 8px' : '8px 12px',
        borderRadius: compact ? 6 : 10,
        background: clickable ? 'transparent' : '#ffffff',
        border: clickable ? '1px solid #c4b5fd' : '1px solid #e2e8f0',
        boxSizing: 'border-box',
        boxShadow: clickable ? '0 1px 2px rgba(109, 40, 217, 0.08)' : undefined,
        cursor: clickable ? 'pointer' : undefined,
        textAlign: 'left',
        transition: clickable ? 'border-color 0.15s, background 0.15s, box-shadow 0.15s' : undefined,
        margin: 0,
        font: 'inherit',
        color: 'inherit',
        display: 'flex',
        alignItems: 'center',
    };

    const columns = compact
        ? [
            showPoints ? 'auto' : null,
            showVisibility ? '136px' : null,
            showVisibility ? '72px' : null,
            '128px',
            'minmax(90px, 1.2fr)',
            (clickable || props.trailing) ? '96px' : null,
        ].filter(Boolean).join(' ')
        : [
            showPoints ? 'auto' : null,
            showVisibility ? '140px' : null,
            'minmax(0, 1fr)',
            (clickable || props.trailing) ? '120px' : null,
        ].filter(Boolean).join(' ');

    const labelStyle: CSSProperties = {
        fontSize: 11,
        color: '#64748b',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        lineHeight: 1,
    };

    const content = (
        <div style={{
            display: 'grid',
            gridTemplateColumns: columns,
            alignItems: 'center',
            columnGap: compact ? 8 : 20,
            width: '100%',
        }}>
            {showPoints && (
                <span style={{
                    color: '#64748b',
                    fontSize: compact ? 11 : 15,
                    fontWeight: 700,
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                }}>
                    {props.green} / {props.total}
                </span>
            )}

            {compact ? (
                <>
                    {showVisibility && (
                        <>
                            <span style={labelStyle}>Radio de visibilidad:</span>
                            <span style={{
                                ...getVisibilityRadiusBadgeStyle(props.percentColor, true),
                                justifySelf: 'start',
                            }}>
                                {props.visibilityRadius ?? 0} mts.
                            </span>
                        </>
                    )}
                    <span style={labelStyle}>Presencia Localpack:</span>
                    <Progress
                        size="small"
                        percent={props.presencePercent}
                        showInfo
                        strokeColor="LimeGreen"
                        trailColor="#e2e8f0"
                        strokeWidth={3}
                        format={(p) => (
                            <span style={{
                                color: '#334155',
                                fontWeight: 700,
                                fontSize: 11,
                            }}>
                                {p}%
                            </span>
                        )}
                        style={{ margin: 0, width: '100%', minWidth: 0, lineHeight: 1 }}
                    />
                </>
            ) : (
                <>
                    {showVisibility && (
                        <div style={{ minWidth: 0, lineHeight: 1 }}>
                            <div style={{
                                fontSize: 10,
                                color: '#475569',
                                fontWeight: 600,
                                letterSpacing: '0.03em',
                                textTransform: 'uppercase',
                                marginBottom: 2,
                                whiteSpace: 'nowrap',
                            }}>
                                Radio de visibilidad
                            </div>
                            <span style={getVisibilityRadiusBadgeStyle(props.percentColor)}>
                                {props.visibilityRadius ?? 0} mts.
                            </span>
                        </div>
                    )}
                    <div style={{ minWidth: 0, lineHeight: 1 }}>
                        <div style={{
                            fontSize: 10,
                            color: '#475569',
                            fontWeight: 600,
                            letterSpacing: '0.03em',
                            textTransform: 'uppercase',
                            marginBottom: 4,
                        }}>
                            Presencia Localpack
                        </div>
                        <Progress
                            size="small"
                            percent={props.presencePercent}
                            showInfo
                            strokeColor="LimeGreen"
                            trailColor="#e2e8f0"
                            strokeWidth={7}
                            format={(p) => (
                                <span style={{
                                    color: '#334155',
                                    fontWeight: 700,
                                    fontSize: 13,
                                }}>
                                    {p}%
                                </span>
                            )}
                            style={{ margin: 0, width: '100%', maxWidth: '100%', lineHeight: 1 }}
                        />
                    </div>
                </>
            )}

            {clickable && (
                <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 4,
                    minWidth: 0,
                    color: '#7e22ce',
                    borderLeft: '1px solid #c4b5fd',
                    paddingLeft: compact ? 8 : 12,
                    marginLeft: compact ? 2 : 4,
                    alignSelf: 'stretch',
                }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: 0,
                        lineHeight: 1.15,
                        minWidth: 0,
                    }}>
                        <span style={{
                            fontSize: compact ? 10 : 12,
                            fontWeight: 600,
                            color: '#7e22ce',
                            textDecoration: 'underline',
                            textDecorationColor: 'rgba(126, 34, 206, 0.25)',
                            textUnderlineOffset: 2,
                            whiteSpace: 'nowrap',
                        }}>
                            Ver Detalle
                        </span>
                        {launchedRelative && (
                            <span style={{
                                fontSize: compact ? 9 : 11,
                                fontWeight: 500,
                                color: '#94a3b8',
                                whiteSpace: 'nowrap',
                            }}>
                                {launchedRelative}
                            </span>
                        )}
                    </div>
                    <RightOutlined style={{ fontSize: compact ? 10 : 11, color: '#7e22ce', flexShrink: 0 }} />
                </div>
            )}

            {props.trailing}
        </div>
    );

    if (clickable) {
        return (
            <button
                type="button"
                onClick={props.onClick}
                style={baseStyle}
                onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#faf5ff';
                    e.currentTarget.style.borderColor = '#a78bfa';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(109, 40, 217, 0.16)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderColor = '#c4b5fd';
                    e.currentTarget.style.boxShadow = '0 1px 2px rgba(109, 40, 217, 0.08)';
                }}
            >
                {content}
            </button>
        );
    }

    return (
        <div style={baseStyle}>
            {content}
        </div>
    );
};

/** 0 cerrado · 1 cierra pronto · 2 abierto · null sin datos */
export const getOpenStatusValue = (record: any): number | null => {
    const value = record?.openStatus ?? record?.isOpen;
    if (value === 0 || value === 1 || value === 2) return value;
    if (value === '0' || value === '1' || value === '2') return Number(value);
    if (value === true) return 2;
    if (value === false) return 0;
    return null;
};

export const getOpenStatusLabel = (record: any): string => {
    const status = getOpenStatusValue(record);
    if (status === 2) return 'Abierto';
    if (status === 1) return 'Cierra pronto';
    if (status === 0) return 'Cerrado';
    return 'Sin datos';
};

export const getOpenStatusColor = (record: any): string => {
    const status = getOpenStatusValue(record);
    if (status === 2) return 'teal';
    if (status === 1) return '#d97706';
    if (status === 0) return '#dc2626';
    return '#94a3b8';
};

export const OpenStatusBadge = ({ record }: { record: any }) => {
    const status = getOpenStatusValue(record);
    const label = getOpenStatusLabel(record);
    const color = getOpenStatusColor(record);

    if (status === 1) {
        return (
            <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: 11,
                fontWeight: 700,
                color: '#b45309',
                background: '#fffbeb',
                border: '1px solid #fbbf24',
                borderRadius: 6,
                padding: '1px 7px',
                lineHeight: 1.4,
            }}>
                {label}
            </span>
        );
    }

    return (
        <span style={{ fontWeight: 600, color }}>
            {label}
        </span>
    );
};

const openStatusFillColor = (record: any): string => {
    const status = getOpenStatusValue(record);
    if (status === 2) return '#14b8a6';
    if (status === 1) return '#f59e0b';
    if (status === 0) return '#ef4444';
    return '#94a3b8';
};

/** Indicador compacto de horario (delante del nombre en tablas). */
export const OpenStatusColorBadge = ({ record }: { record: any }) => {
    const label = getOpenStatusLabel(record);

    return (
        <Tooltip title={`Horario: ${label}`}>
            <span
                aria-label={label}
                style={{
                    display: 'inline-block',
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: openStatusFillColor(record),
                    flexShrink: 0,
                }}
            />
        </Tooltip>
    );
};
